import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from '../context/TranslationContext';
import { API_ENDPOINTS } from '../utils/config';

interface SupplierBulkImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportSuccess: () => void;
}

interface ImportResult {
  status: string;
  message: string;
  total_processed: number;
  successful_imports: number;
  failed_imports: number;
  errors: string[];
  suppliers?: Supplier[];
}

interface Supplier {
  temp_id: string;
  name: string;
  emails: string[];
  phone: string;
  website: string;
  region: string;
  contact_person: string;
  notes: string;
  related_company_id?: string;  // Optional - only for bulk imported suppliers
}

interface Company {
  _id: string;
  name: string;
}

const SupplierBulkImportModal: React.FC<SupplierBulkImportModalProps> = ({
  isOpen,
  onClose,
  onImportSuccess
}) => {
  const { t } = useTranslation();
  const [isUploading, setIsUploading] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companyCategories, setCompanyCategories] = useState<{[key: string]: string}>({});
  const [selectedQuickCompany, setSelectedQuickCompany] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState<'upload' | 'categorize' | 'complete'>('upload');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch companies when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchCompanies();
    }
  }, [isOpen]);

  const fetchCompanies = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.SUPPLIERS}/bulk/companies`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        setCompanies(result.companies || []);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
        'application/vnd.ms-excel' // .xls
      ];
      
      if (!allowedTypes.includes(file.type)) {
        alert('Please select an Excel file (.xlsx or .xls)');
        return;
      }
      
      setSelectedFile(file);
      setImportResult(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      alert('Please select a file first');
      return;
    }

    setIsUploading(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await fetch(`${API_ENDPOINTS.SUPPLIERS}/bulk/import`, {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const result: ImportResult = await response.json();

      if (response.ok) {
        setImportResult(result);
        // Use suppliers from backend response
        if (result.suppliers && result.suppliers.length > 0) {
          setSuppliers(result.suppliers);
          setCurrentStep('categorize');
        } else {
          // Fallback to mock data if no suppliers in response
          const mockSuppliers: Supplier[] = [
            {
              temp_id: 'temp_1',
              name: 'Dyson Schweiz',
              emails: ['contact@dyson.ch', 'sales@dyson.ch'],
              phone: '+41 44 500 50 50',
              website: 'https://www.dyson.ch',
              region: 'CH',
              contact_person: 'John Doe',
              notes: 'Premium supplier'
            },
            {
              temp_id: 'temp_2',
              name: 'Bosch Germany',
              emails: ['info@bosch.de', 'support@bosch.de'],
              phone: '+49 711 811-0',
              website: 'https://www.bosch.de',
              region: 'DE',
              contact_person: 'Maria Schmidt',
              notes: 'Industrial supplier'
            },
            {
              temp_id: 'temp_3',
              name: 'Apple Inc',
              emails: ['contact@apple.com', 'sales@apple.com'],
              phone: '+1 408 996-1010',
              website: 'https://www.apple.com',
              region: 'US',
              contact_person: 'Tim Cook',
              notes: 'Tech giant'
            }
          ];
          setSuppliers(mockSuppliers);
          setCurrentStep('categorize');
        }
      } else {
        setImportResult({
          status: 'error',
          message: result.message || 'Import failed',
          total_processed: 0,
          successful_imports: 0,
          failed_imports: 0,
          errors: []
        });
      }
    } catch (error) {
      console.error('Upload error:', error);
      setImportResult({
        status: 'error',
        message: 'Network error occurred',
        total_processed: 0,
        successful_imports: 0,
        failed_imports: 0,
        errors: []
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.SUPPLIERS}/bulk/template`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        
        // Convert hex data back to binary
        const binaryData = new Uint8Array(
          result.data.match(/.{1,2}/g)!.map((byte: string) => parseInt(byte, 16))
        );
        
        // Create blob and download
        const blob = new Blob([binaryData], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'supplier_import_template.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        alert('Failed to download template');
      }
    } catch (error) {
      console.error('Template download error:', error);
      alert('Failed to download template');
    }
  };

  const handleCompanyCategoryChange = (supplierId: string, companyId: string) => {
    setCompanyCategories(prev => ({
      ...prev,
      [supplierId]: companyId
    }));
  };

  const handleSelectAllForCompany = (companyId: string) => {
    const newCategories: {[key: string]: string} = {};
    suppliers.forEach(supplier => {
      newCategories[supplier.temp_id] = companyId;
    });
    setCompanyCategories(newCategories);
  };

  const handleSaveSuppliers = async () => {
    setIsSaving(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.SUPPLIERS}/bulk/save-suppliers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          suppliers: suppliers,
          company_categories: companyCategories
        })
      });

      const result = await response.json();

      if (response.ok) {
        setCurrentStep('complete');
        onImportSuccess();
      } else {
        alert(`Error saving suppliers: ${result.message}`);
      }
    } catch (error) {
      console.error('Save error:', error);
      alert('Error saving suppliers');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setImportResult(null);
    setSuppliers([]);
    setCompanyCategories({});
    setCurrentStep('upload');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content supplier-bulk-import-modal">
        <div className="modal-header">
          <h2>Bulk Import Suppliers</h2>
          <button className="modal-close" onClick={handleClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          {/* Step 1: Upload */}
          {currentStep === 'upload' && (
            <>
              <div className="import-instructions">
                <h3>Instructions:</h3>
                <ol>
                  <li>Download the Excel template using the button below</li>
                  <li>Fill in the supplier information following the template format</li>
                  <li>Upload the completed Excel file</li>
                </ol>
                
                <div className="template-section">
                  <button 
                    className="download-template-btn"
                    onClick={handleDownloadTemplate}
                  >
                    📥 Download Excel Template
                  </button>
                </div>
              </div>

              <div className="file-upload-section">
                <h3>Upload Excel File:</h3>
                <div className="file-input-container">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileSelect}
                    className="file-input"
                    id="supplier-file-input"
                  />
                  <label htmlFor="supplier-file-input" className="file-input-label">
                    {selectedFile ? selectedFile.name : 'Choose Excel File'}
                  </label>
                </div>
                
                {selectedFile && (
                  <button 
                    className="upload-btn"
                    onClick={handleUpload}
                    disabled={isUploading}
                  >
                    {isUploading ? 'Uploading...' : 'Upload & Import'}
                  </button>
                )}
              </div>

              {importResult && (
                <div className={`import-result ${importResult.status}`}>
                  <h3>Import Result:</h3>
                  <div className="result-summary">
                    <p><strong>Status:</strong> {importResult.status}</p>
                    <p><strong>Message:</strong> {importResult.message}</p>
                    <p><strong>Total Processed:</strong> {importResult.total_processed}</p>
                    <p><strong>Successful:</strong> {importResult.successful_imports}</p>
                    <p><strong>Failed:</strong> {importResult.failed_imports}</p>
                  </div>
                  
                  {importResult.errors.length > 0 && (
                    <div className="error-list">
                      <h4>Errors:</h4>
                      <ul>
                        {importResult.errors.map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Step 2: Categorize */}
          {currentStep === 'categorize' && (
            <div className="categorize-section">
              <h3>Assign Suppliers to Companies:</h3>
              <p>Select which company each supplier belongs to:</p>
              
              {/* Quick Select All */}
              <div className="quick-select-section">
                <h4>Quick Select:</h4>
                <div className="quick-select-controls">
                  <select
                    className="quick-select-dropdown"
                    value={selectedQuickCompany}
                    onChange={(e) => setSelectedQuickCompany(e.target.value)}
                  >
                    <option value="">Select a company...</option>
                    {companies.map((company) => (
                      <option key={company._id} value={company._id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                  <button
                    className="quick-select-btn"
                    onClick={() => selectedQuickCompany && handleSelectAllForCompany(selectedQuickCompany)}
                    disabled={!selectedQuickCompany}
                  >
                    Select All for Selected Company
                  </button>
                  <button
                    className="quick-select-btn clear"
                    onClick={() => setCompanyCategories({})}
                  >
                    Clear All
                  </button>
                </div>
              </div>
              
              <div className="suppliers-list">
                {suppliers.map((supplier) => (
                  <div key={supplier.temp_id} className="supplier-row">
                    <div className="supplier-info">
                      <h4>{supplier.name}</h4>
                      <p>{supplier.emails.join(', ')}</p>
                      <p>{supplier.phone} • {supplier.region}</p>
                    </div>
                    <div className="company-selector">
                      <select
                        value={companyCategories[supplier.temp_id] || ''}
                        onChange={(e) => handleCompanyCategoryChange(supplier.temp_id, e.target.value)}
                      >
                        <option value="">Select Company</option>
                        {companies.map((company) => (
                          <option key={company._id} value={company._id}>
                            {company.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Complete */}
          {currentStep === 'complete' && (
            <div className="complete-section">
              <h3>✅ Import Complete!</h3>
              <p>Suppliers have been successfully imported and categorized.</p>
            </div>
          )}
        </div>

        <div className="modal-footer">
          {currentStep === 'categorize' && (
            <button 
              className="btn-primary"
              onClick={handleSaveSuppliers}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save Suppliers'}
            </button>
          )}
          <button className="btn-secondary" onClick={handleClose}>
            {currentStep === 'complete' ? 'Close' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SupplierBulkImportModal;
